import React from 'react';
import {WithTranslation, withTranslation} from 'react-i18next';
import {connect} from 'react-redux';
import {services} from '../../../../application/service/services';
import {TableDataset} from '../../../../component/table/Table';
import {VisualizationInstance} from '../../../model/viz/VisualizationInstance';
import {getDashboardResolvedParams} from '../../../state/selectors/dashboardSelector';
import './GroupTableViz.scss';
import {ResolvedParams} from "../../../model/params/resolve/ResolvedParams";
import {ClickableViz} from "../ClickableViz";
import {Icon} from "@blueprintjs/core";
import {
    AbstractTableVizComponent,
    AbstractTableVizStateProps,
    Column,
    ColumnRenderer,
    TableItem,
    TableItemUI
} from "../table/AbstractTableViz";
import _ from "lodash";
import {SortOrder} from "../../../../utils/query/SortOrder";
import {DataRenderer} from "../../../../component/table/DataRenderer";


interface GroupTableVizStateProps extends AbstractTableVizStateProps {
    collapsedSections: Set<number>;
    orderColumnIndex: null,
    orderDirection: null,
}


interface GroupTableVizProps extends WithTranslation {
    viz: VisualizationInstance,
    dashboardResolvedParams: ResolvedParams,
    openFeedbacksPanel: (viz: ClickableViz, panelTitle: string) => void
}

export class GroupTableVizComponent extends AbstractTableVizComponent<GroupTableVizProps> {
    private dataRenderer: DataRenderer;

    constructor(props: GroupTableVizProps, context: any) {
        super(props, context);
        this.dataRenderer = new DataRenderer(this.props.viz, this.props.t);
    }

    onRowClick(event, row: TableItemUI): void {
        this.state.clickItem = row;
        const {t} = this.props;
        const field = services.getFieldsService().getField(row.field);
        const fieldValue = services.getFieldsService().findFieldValue(field.name, row.fieldValue);
        this.props.openFeedbacksPanel(this, fieldValue ? fieldValue.getLabel(t) : row.fieldValue);
        event.preventDefault();
        event.stopPropagation();
    }

    getColumnRender(type: string): ColumnRenderer {
        return this.dataRenderer.dataColumnRenderers[type];
    }

    state: GroupTableVizStateProps = {
        ...this.state,
        collapsedSections: new Set<number>()
    };


    directionGetter = (columnName: string): SortOrder => {
        if (!this.state.sorting || this.state.sorting?.columnName !== columnName) {
            return null;
        }
        return this.state.sorting.direction;
    }

    toggleSection = (index: number) => {
        const {collapsedSections} = this.state;
        const newCollapsedSections = new Set(collapsedSections);
        if (newCollapsedSections.has(index)) {
            newCollapsedSections.delete(index);
        } else {
            newCollapsedSections.add(index);
        }
        this.setState({collapsedSections: newCollapsedSections});
    };

    renderCellContent = (column: Column, cellValue: TableItemUI) => {
        const renderer = column.dataRenderer;
        return renderer(cellValue, column);
    };

    renderRow = (row: TableItemUI, isLastRow: boolean, onClick?: (event) => void) => {
        // return cells as direct grid children so all rows align to the container grid
        const dynamicColumns = this.getDynamicColumns();

        const columnValue = this.getAggregationRowFieldValue(row);

        const cells: React.ReactNode[] = [];
        const rowClasses = isLastRow ? ' last-row' : ''

        // aggregations cell (left aligned)
        cells.push(
            <div key={`aggs-${row.field}-${row.fieldValue}`} className={"row-aggregations" + rowClasses}
                 onClick={onClick}>
                <span key={`aggs-${row.field}-${row.fieldValue}-span`}
                      className="aggregation-value">{columnValue}</span>
            </div>
        );

        // dynamic data columns (right aligned)
        dynamicColumns.forEach(([key, column], index) => {
            const classes = index === dynamicColumns.length - 1 ? ' last-cell' : '';
            cells.push(
                <div key={`cell-${column.name}-${row.field}-${row.fieldValue}`}
                     className={`row-cell row-cell-${column["@type"]}` + classes + rowClasses} onClick={onClick}>
                    {this.renderCellContent(column, row)}
                </div>
            );
        });


        return cells;
    };

    private getDynamicColumns(includeAgg = false) {
        const uiColumns = this.props.viz.definition.uiParams.columns;
        return Object.entries(this.columns)
            .filter(([key, col]) => (includeAgg || col["@type"] !== 'aggregation') &&
                (!uiColumns
                    || !uiColumns[col.name]
                    || uiColumns[col.name].visible !== false));
    }

    private

    getAggregationRowFieldValue(section: TableItemUI) {
        const {t} = this.props;
        const field = services.getFieldsService().getField(section.field);
        const fieldValues = services.getFieldsService().getFieldValues(field.name);

        const columnValue = fieldValues && fieldValues.find(section.fieldValue) ? fieldValues.find(section.fieldValue).getLabel(t) : section.fieldValue;
        return columnValue;
    }

    renderSectionHeader = (section: TableItemUI, index: number) => {
        const {collapsedSections} = this.state;
        const isCollapsed = collapsedSections.has(index);
        const columnValue = this.getAggregationRowFieldValue(section);
        const color = services.getColorService().getColorForFieldValue(section.field, section.fieldValue);
        const volume = this.getVolume(section).count;
        const style = {
            gridColumn: '1 / -1',
            backgroundColor: color + '33'  // 33 is for opacity
        };
        if (isCollapsed)
            style['borderRadius'] = '10px';
        else {
            style['borderTopLeftRadius'] = '10px';
            style['borderTopRightRadius'] = '10px';
        }
        return (
            <div
                key={`section-header-${index}`}
                className="section-header"
                onClick={() => this.toggleSection(index)}
                style={style}
            >
                <Icon
                    icon={isCollapsed ? 'chevron-down' : 'chevron-right'}
                    className="section-toggle-icon"
                />
                <div
                    className="section-icon"
                    style={{backgroundColor: color}}
                />
                <span className="section-title" style={{color}}>{columnValue}</span>
                <span className="section-count">{volume} occurrences</span>
            </div>
        );
    }

    protected getVolume(item: TableItem): { count: number, percent: number } {
        return item.data.find(
            x => x.name === "volume"
        )?.value;
    }

    renderTable(uiParams) {
        const uiData: TableDataset<TableItemUI> = this.getUiDataset();

        // compute dynamic columns once so all sections share same layout
        const dynamicColumns = this.getDynamicColumns();

        const aggCol = `max-content`;
        const dynamicColsTemplate = dynamicColumns.map(([name, column]) => this.getColumnRender(name).width ? this.getColumnRender(name).width : '1fr').join(' ');
        const gridTemplateColumns = `${aggCol} ${dynamicColsTemplate}`;


        const rows: React.ReactNode[] = [];

        rows.push(this.renderHeader())

        uiData.data
            .filter(value => value.children && value.children.length > 0)
            .forEach((section, idx) => {

                const isCollapsed = this.state.collapsedSections.has(idx);
                rows.push(this.renderSectionHeader(section, idx));

                if (!isCollapsed) {
                    section.children.forEach((row, childIdx) => {
                        const isLast = childIdx === section.children.length - 1;
                        const rowCells = this.renderRow(row, isLast, (event) => this.onRowClick(event, row));
                        // renderRow returns an array of cells; spread so each cell is a grid child
                        if (Array.isArray(rowCells)) {
                            rows.push(...rowCells);
                        } else {
                            rows.push(rowCells);
                        }
                    });
                }
            });

        return (
            <div className="distribution-grouped-table" style={{display: 'grid', gridTemplateColumns}}>
                {rows}
            </div>
        );
    }

    renderHeader() {

        // return cells as direct grid children so all rows align to the container grid
        const dynamicColumns = this.getDynamicColumns(true);

        const firstLevelAgg = Object.entries(this.columns)
            .find(([key, col]) => col["@type"] === 'aggregation');

        const cells: JSX.Element[] = [];


        // dynamic data columns (right aligned)
        dynamicColumns.forEach(([key, column], colIndex) => {
            if (key === firstLevelAgg[0]) return;
            const renderer = column.headerRenderer;
            if (_.isNil(renderer)) {
                cells.push(<div></div>)
            } else {
                cells.push(renderer(column, colIndex, this.directionGetter(column.name),
                    ((columnName, columnIndex, sortDirection, event) => {
                        this.onSort(columnName, sortDirection)
                    }).bind(this)));
            }
        });


        return cells;
    }
}

const mapStateToProps = (state) => {
    return {
        dashboardResolvedParams: getDashboardResolvedParams(state),
        resolvedSpec: state.dashboard.resolvedSpec
    };
};


const mapDispatchToProps = (dispatch, props) => {
    return {
        openFeedbacksPanel: (viz: ClickableViz, panelTitle: string) => {
            dispatch.dashboard.openFeedBackPanel({viz, panelTitle});
        },
    }
};


export const GroupTableViz = connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true})(withTranslation(undefined, {withRef: true})(GroupTableVizComponent));


GroupTableViz['getLabels'] = AbstractTableVizComponent.getLabels